import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./tabs.scss";
import { useAuth, useSort, useTabs } from "@Hooks";
import { IPreviewTab, ISharedFullConversation, TabSizeEnum } from "atlas-shared";
import { TabMaximizeWrapper } from "./tab.maximize";
import { deleteTab, patchTab, readConversation } from "@Api";
import { useTranslation } from "react-i18next";
import {
  actionSetFocusedConversation,
  actionSetHandlingConversation,
  actionUpdateTab,
  useAppDispatch,
} from "@Store";
import { setPreventDefault } from "@Utils";
import { TabMini } from "@Components/tab/tab.mini";
import { VoiceInternals } from "@Components/tab/voice-internals/voice-internals";
import { EmergencyTabs } from "./emergency";
import { useNavigate } from "react-router-dom";

export const Tabs = React.memo(() => {
  const TABS_LIMIT = 7;
  const tabs = useTabs();
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [optimisticTabs, setOptimisticTabs] = useState<IPreviewTab[]>([]);
  // Track pending tab size updates to avoid flicker when api responses come back
  const [pendingTabUpdates, setPendingTabUpdates] = useState<{ [tabId: string]: TabSizeEnum }>({});

  const clearPendingTabUpdate = (tabId: string) => {
    setPendingTabUpdates((prev) => {
      const { [tabId]: _, ...rest } = prev;
      return rest;
    });
  };

  // Keep optimisticTabs in sync with backend tabs, but merge in any pending updates
  useEffect(() => {
    setOptimisticTabs(
      tabs.tabs.map((t) => (pendingTabUpdates[t.id] ? { ...t, size: pendingTabUpdates[t.id] } : t))
    );
  }, [tabs.tabs, pendingTabUpdates]);

  // Filtered tabs for rendering
  const filteredTabs = useMemo(() => {
    const allTabs = [...optimisticTabs]
      .sort((a, b) => a.order - b.order)
      .filter((t) => t.agentprofile_id === auth.user_status.agentprofile_id);
    return {
      active: allTabs.slice(0, TABS_LIMIT),
      inactive: allTabs.slice(TABS_LIMIT, allTabs.length).length,
    };
  }, [optimisticTabs, auth.user_status.agentprofile_id]);

  const { onDragStart, onDragEnd, onDragOver, canDrop } = useSort({
    dropElement: <div className="drop-element">{t("DROP_HERE")}</div>,
    value: filteredTabs.active,
    onChange: (new_order) => {
      new_order.forEach((new_tab, index) => {
        if (new_tab.order !== index) {
          dispatch(actionUpdateTab({ ...new_tab, order: index }));
          patchTab(new_tab.id, {
            order: index,
          });
        }
      });
    },
  });

  const expandedTab = useMemo(
    () => optimisticTabs.find((t) => t.size === TabSizeEnum.Maximized),
    [optimisticTabs]
  );

  const updateTabSizeOptimistically = (tabId: string, newSize: TabSizeEnum) => {
    setPendingTabUpdates((prev) => ({ ...prev, [tabId]: newSize }));
    setOptimisticTabs((prevTabs) =>
      prevTabs.map((t) => (String(t.id) === String(tabId) ? { ...t, size: newSize } : t))
    );
  };

  const onClose = (tab: IPreviewTab): Promise<IPreviewTab> => {
    setOptimisticTabs((prevTabs) => prevTabs.filter((t) => t.id !== tab.id));
    return new Promise((resolve) => {
      deleteTab(tab.id)
        .catch(() => setOptimisticTabs([...tabs.tabs]))
        .finally(() => resolve(tab));
    });
  };

  const onHeaderClick = useCallback(
    (tab: IPreviewTab) => {
      if (expandedTab) {
        if (expandedTab.id !== tab.id) {
          updateTabSizeOptimistically(String(expandedTab.id), TabSizeEnum.Minimized);
          updateTabSizeOptimistically(String(tab.id), TabSizeEnum.Maximized);
          patchTab(expandedTab.id, { size: TabSizeEnum.Minimized })
            .catch(() => setOptimisticTabs([...tabs.tabs]))
            .finally(() => clearPendingTabUpdate(String(expandedTab.id)));
          return patchTab(tab.id, { size: TabSizeEnum.Maximized })
            .then(() => tab)
            .catch(() => {
              setOptimisticTabs([...tabs.tabs]);
              return tab;
            })
            .finally(() => clearPendingTabUpdate(String(tab.id)));
        } else {
          updateTabSizeOptimistically(String(tab.id), TabSizeEnum.Minimized);
          return patchTab(tab.id, { size: TabSizeEnum.Minimized })
            .then(() => tab)
            .catch(() => {
              setOptimisticTabs([...tabs.tabs]);
              return tab;
            })
            .finally(() => clearPendingTabUpdate(String(tab.id)));
        }
      } else {
        if (tab.size === TabSizeEnum.Minimized) {
          updateTabSizeOptimistically(String(tab.id), TabSizeEnum.Mini);
          return patchTab(tab.id, { size: TabSizeEnum.Mini })
            .then(() => tab)
            .catch(() => {
              setOptimisticTabs([...tabs.tabs]);
              return tab;
            })
            .finally(() => clearPendingTabUpdate(String(tab.id)));
        } else {
          navigate(`/dashboard/conversation/${tab.organization_id}/${tab.conversation_id}`);
          updateTabSizeOptimistically(String(tab.id), TabSizeEnum.Minimized);
          return patchTab(tab.id, { size: TabSizeEnum.Minimized })
            .then(() => tab)
            .catch(() => {
              setOptimisticTabs([...tabs.tabs]);
              return tab;
            })
            .finally(() => clearPendingTabUpdate(String(tab.id)));
        }
      }
    },
    [expandedTab, tabs, navigate]
  );

  const onMinimize = useCallback(
    (tab: IPreviewTab) => {
      updateTabSizeOptimistically(String(tab.id), TabSizeEnum.Minimized);
      return patchTab(tab.id, { size: TabSizeEnum.Minimized })
        .then(() => tab)
        .catch(() => {
          setOptimisticTabs([...tabs.tabs]);
          return tab;
        })
        .finally(() => clearPendingTabUpdate(String(tab.id)));
    },
    [tabs]
  );

  const onMaximize = useCallback(
    (tab: IPreviewTab) => {
      const maximizedTabs = [...optimisticTabs].filter((t) => t.size === TabSizeEnum.Maximized);
      maximizedTabs.forEach((t) =>
        updateTabSizeOptimistically(String(t.id), TabSizeEnum.Minimized)
      );
      updateTabSizeOptimistically(String(tab.id), TabSizeEnum.Maximized);
      maximizedTabs.forEach((t) =>
        patchTab(t.id, { size: TabSizeEnum.Minimized })
          .catch(() => setOptimisticTabs([...tabs.tabs]))
          .finally(() => clearPendingTabUpdate(String(t.id)))
      );
      return patchTab(tab.id, { size: TabSizeEnum.Maximized })
        .then(() => tab)
        .catch(() => {
          setOptimisticTabs([...tabs.tabs]);
          return tab;
        })
        .finally(() => clearPendingTabUpdate(String(tab.id)));
    },
    [optimisticTabs, tabs]
  );

  const onMini = useCallback(
    (tab: IPreviewTab) => {
      updateTabSizeOptimistically(String(tab.id), TabSizeEnum.Mini);
      return patchTab(tab.id, { size: TabSizeEnum.Mini })
        .then(() => tab)
        .catch(() => {
          setOptimisticTabs([...tabs.tabs]);
          return tab;
        })
        .finally(() => clearPendingTabUpdate(String(tab.id)));
    },
    [tabs]
  );

  const onFocus = useCallback(
    (tab: IPreviewTab, conversation: ISharedFullConversation) => {
      if (conversation.user_id === auth.user.id && conversation.read < conversation.unread)
        readConversation(conversation.organization_id, conversation.id);

      dispatch(actionSetFocusedConversation(conversation.id));
      dispatch(actionSetHandlingConversation(conversation.id));
    },
    [auth.user.id, dispatch]
  );

  const hasVoice = useMemo(() => auth?.user.main && auth?.user.voice_license, [auth]);

  return (
    <div className={"tabs-wrapper"}>
      <EmergencyTabs t={t} />
      {hasVoice && <VoiceInternals />}
      <div
        className={"tabs"}
        onDrop={setPreventDefault}
        onDragOver={setPreventDefault}
        draggable={false}
      >
        {!!filteredTabs.inactive && (
          <div id="inactive-tabs">
            <h1>{filteredTabs.inactive}</h1>
            <div className="line-1">{t("INACTIVE_TABS_HIDDEN")}</div>
            <div className="line-2">{t("INACTIVE_TABS_DESCRIPTION")}</div>
          </div>
        )}
        {filteredTabs.active.map((tab, index) => {
          return (
            <React.Fragment key={tab.id}>
              {canDrop(index, true)}
              <TabMini
                auth={auth}
                tab={tab}
                organization_id={tab.organization_id}
                conversation_id={tab.conversation_id}
                index={index}
                expandedTab={expandedTab}
                onClose={() => onClose(tab)}
                onMaximize={() => onMaximize(tab)}
                onHeaderClick={() => onHeaderClick(tab)}
                onMinimizeClick={() => onMinimize(tab)}
                onFocus={(conversation) => onFocus(tab, conversation)}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDragOver={onDragOver}
              />
              {canDrop(index, false)}
            </React.Fragment>
          );
        })}
      </div>
      {filteredTabs.active.map((tab) => {
        return (
          <TabMaximizeWrapper
            key={tab.id}
            auth={auth}
            visible={expandedTab?.id === tab.id}
            onClose={() => onClose(tab)}
            onMinimize={() => onMinimize(tab)}
            onMini={() => onMini(tab)}
            organization_id={tab.organization_id}
            conversation_id={tab.conversation_id}
            tab={tab}
            onFocus={(conversation) => onFocus(tab, conversation)}
            absolute={true}
          />
        );
      })}
    </div>
  );
});
